import { ReactComponent as CloseLogoWhite } from '@percent/cause-dashboard/common/assets/images/close-icon-white.svg'
import { ReactComponent as CloseLogoBlack } from '@percent/cause-dashboard/common/assets/images/close.svg'
import styles from './ApplyModalContainer.module.scss'
import { useCallback, useEffect, useState } from 'react'
import { Modal } from '@percent/lemonade'
import { ApplyModalContent } from './content/ApplyModalContent'
import { useAuthState } from '@percent/cause-dashboard/common/hooks'
import type { SmartApplyPartner, SmartApplyPartnerConfigKey } from '@percent/cause-dashboard/constants/smartApplyPartners'

export type ApplyModalSource = 'product-offers-detail' | 'smart-apply'

export type ApplyModalCommonProps = {
  modalTitle: string
  modalDescription: string
  disclaimer: string
  source?: ApplyModalSource
  partner?: SmartApplyPartner
  experimentVariant?: string
  externalApplyUrl?: string
  products: {
    name: SmartApplyPartnerConfigKey | 'meta' | 'hootsuite'
    displayName: string
    description: string
    logo: string
    isOptional: boolean
  }[]
}

type ModalContainerProps = {
  isOpen: boolean
  handleClose: () => void
  image: string
  onSuccess?: () => void
}

const getModalWidth = (widthStep: number, modalWidth: number) =>
  window.innerWidth > widthStep ? modalWidth : window.innerWidth * 0.95
const getModalHeight = (heightStep: number, modalHeight: number) =>
  window.innerHeight > heightStep ? modalHeight : window.innerHeight * 0.95

export function ApplyModalContainer({
  isOpen,
  handleClose,
  image,
  onSuccess,
  ...commonProps
}: Readonly<ModalContainerProps & ApplyModalCommonProps>) {
  const { authState } = useAuthState()
  const [_firstName, ..._lastNameParts] = authState.user?.fullName?.trim()
    ? authState.user.fullName.trim().split(' ')
    : ['']
  const hasBothNames = !!_firstName && !!_lastNameParts.join(' ').trim()
  const hasMultipleProducts = commonProps.products.length > 1

  const baseModalHeight = hasBothNames || !hasMultipleProducts ? 547 : 660
  const heightStep = hasBothNames || !hasMultipleProducts ? 600 : 700
  const widthStep = 1000

  const [modalWidth, setModalWidth] = useState(getModalWidth(widthStep, 960))
  const [modalHeight, setModalHeight] = useState(getModalHeight(heightStep, baseModalHeight))
  const [isApplied, setIsApplied] = useState(false)

  const onCloseHanlder = useCallback(() => {
    if (isApplied) {
      onSuccess?.()
    }
    handleClose()
  }, [isApplied, onSuccess, handleClose])

  const onApplyExternalHandler = useCallback(() => {
    onSuccess?.()
    handleClose()
  }, [onSuccess, handleClose])

  useEffect(() => {
    const handleResize = () => {
      setModalWidth(getModalWidth(widthStep, 960))
      setModalHeight(getModalHeight(heightStep, baseModalHeight))
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [baseModalHeight, heightStep, widthStep])

  return (
    <Modal
      open={isOpen}
      data-testid="apply-modal-container"
      width={modalWidth}
      height={modalHeight}
      onClose={onCloseHanlder}
    >
      <div className={styles.modalCtn} style={{ height: modalHeight, width: modalWidth }}>
        <div className={`${styles.column} ${styles.leftColumn}`}>
          <button
            title="Close modal"
            id={styles.btnCloseDialog}
            onClick={onCloseHanlder}
            type="button"
            data-testid="close-button"
          >
            {window.innerWidth > widthStep ? (
              <CloseLogoWhite className={styles.closeLogoWhite} />
            ) : (
              <CloseLogoBlack className={styles.closeLogoBlack} />
            )}
          </button>
          <div className={`${styles.leftCtn} ${styles.lessPadding}`}>
            <ApplyModalContent
              {...commonProps}
              source={commonProps.source || 'product-offers-detail'}
              partner={commonProps.partner}
              experimentVariant={commonProps.experimentVariant}
              setIsApplied={setIsApplied}
              onApplyExternal={onApplyExternalHandler}
            />
          </div>
        </div>
        <div
          className={`${styles.column} ${styles.rightColumn}`}
          style={{ '--modal-right-column-image': `url(${image})` } as React.CSSProperties}
        />
      </div>
    </Modal>
  )
}
